import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Input,
} from '@angular/core';
import { decodeFont, decodeImage, Event, EventType, RiveEventPayload as EventPayload, Rive as Video, StateMachineInput as VideoInput } from '@rive-app/canvas';

@Component({
  selector: 'amaze-player',
  template: `
    <div class="amz-container">
      <canvas
        #canvas
        [width]="orientation === 'portrait' ? 720 : 1280"
        [height]="orientation === 'portrait' ? 1280 : 720"
      ></canvas>
      <div *ngIf="isLoading" class="loader-container">
        <div class="loader"></div>
      </div>

      <button
        *ngIf="!autoplay && !isPlaying && !isLoading"
        (click)="play()"
        class="play-button"
      >
        <svg
          width="50"
          height="50"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 5v14l11-7z" />
        </svg>
      </button>

      <button (click)="toggleMute()" class="mute-button">
        <svg
          *ngIf="isMuted"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16.5 12L21 16.5M21 7.5L16.5 12M4 8v8h4l5 5V3L8 8H4z" />
        </svg>
        <svg
          *ngIf="!isMuted"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      </button>
    <div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
      }

      canvas {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
      }

      .amz-container {
        position: relative;
        width: 100%;
        height: 100%;
      }

      .play-button {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.6);
        border: none;
        border-radius: 50%;
        width: 70px;
        height: 70px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease-in-out;
      }

      .play-button svg {
        fill: white;
      }

      .play-button:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: translate(-50%, -50%) scale(1.1);
      }

      .mute-button {
        position: absolute;
        bottom: 2%;
        right: 2%;
        width: 17%;
        height: 10%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        background: transparent;
        border: none;
        border-radius: 50%;
      }

      .mute-button svg {
        fill: black;
      }

      .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: transparent;
      }

      .loader {
        width: 48px;
        height: 48px;
        border: 5px solid #000;
        border-bottom-color: transparent;
        border-radius: 50%;
        animation: rotation 1s linear infinite;
      }

      @keyframes rotation {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class AmazePlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() src: string | undefined;
  @Input() audioSrc: string | undefined;
  @Input() audioLoop: boolean = true;
  @Input() data: any;
  @Input() config: { scenes: number } = { scenes: 10 };
  @Input() stateMachines: string = 'State Machine';
  @Input() autoplay: boolean = true;
  @Input() loop: boolean = true;
  @Input() assets: { [key: string]: string } = {};
  @Input() orientation: 'landscape' | 'portrait' | undefined;

  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  isPlaying: boolean = false;
  isLoading: boolean = true;
  isMuted: boolean = false;

  constructor() {}

  private video: Video | undefined;
  private audio: HTMLAudioElement | null = null;

  private assetPromises: Promise<any>[] = [];

  ngOnInit(): void {
    if (this.audioSrc) {
      this.audio = new Audio();
      this.audio.src = this.audioSrc;
      this.audio.loop = this.audioLoop;
      this.audio.preload = 'auto';
      this.audio.muted = this.autoplay;
      this.isMuted = this.autoplay;
    }
  }

  ngAfterViewInit(): void {
    if (!this.canvas) {
      console.error('El elemento canvas no se ha inicializado correctamente.');
      return;
    }
    this.initializeVideo();
  }

  ngOnDestroy(): void {
    this.video?.stop();
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio.remove();
      this.audio = null;
    }
  }

  play(): void {
    if (this.video) {
      this.video.play();
      this.audio?.play();
      this.isPlaying = true;
    }
  }

  pause(): void {
    if (this.video) {
      this.video.pause();
      this.audio?.pause();
    }
  }

  toggleMute() {
    if (this.audio) {
      this.isMuted = !this.isMuted;
      this.audio.muted = this.isMuted;
      this.audio.play();
    }
  }

  private setTextsValues(): void {
    if (this.video) {
      Object.keys(this.data).forEach((key) => {
        this.video?.setTextRunValue(key, this.data[key]);
      });
    }
  }

  private setEventHandler(): void {
    if (this.video) {
      this.video.on(EventType.RiveEvent, e => {
        const payload = e.data as EventPayload;
        const handler = this.data[payload.name]
        if (typeof handler === 'function') {
          handler(payload)
        }
			});
    }
  }

  private async setInputsValues(): Promise<void> {
    const inputs = this.video?.stateMachineInputs(this.stateMachines)
    Object.keys(this.data).forEach((key) => {
      const value = this.data[key];

      const input = inputs?.find((i: VideoInput) => i.name === key);
      if (input) {
        input.value = value;
      }

      for (let i = 0; i < this.config.scenes; i++) {
        const artboard = 'artboard ' + (i + 1)
        if (typeof value === 'boolean') {
          this.video?.setBooleanStateAtPath(key, value, artboard)
        } else if (typeof value === 'number') {
          this.video?.setNumberStateAtPath(key, value, artboard)
        }
        this.video?.setTextRunValueAtPath(key, value, artboard)
      }
    });
  }

  private assetLoader(asset: any): boolean {
    if (this.assets[asset.name]) {
      if (asset.isImage) {
        this.assetPromises.push(this.loadImage(asset, this.assets[asset.name]));
      }
      if (asset.isFont) {
        this.assetPromises.push(this.loadFont(asset, this.assets[asset.name]));
      }
      return true;
    }
    return false;
  }

  private async loadImage(asset: any, url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load image');
      const image = await decodeImage(
        new Uint8Array(await response.arrayBuffer())
      );
      asset.setRenderImage(image);
      image.unref();
      return true;
    } catch (error) {
      console.error('Failed to load image', error);
      return false;
    }
  }

  private async loadFont(asset: any, url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load font');
      const font = await decodeFont(
        new Uint8Array(await response.arrayBuffer())
      );
      asset.setFont(font);
      return true;
    } catch (error) {
      console.error('Failed to load font', error);
      return false;
    }
  }

  private async initializeVideo(): Promise<void> {
    if (!this.src) {
      console.error('Video source is not provided.');
      return;
    }

    try {
      const response = await fetch(this.src);
      if (!response.ok) {
        throw new Error('Failed to fetch Video file.');
      }
      const buffer = await response.arrayBuffer();

      this.video = new Video({
        buffer: buffer,
        canvas: this.canvas.nativeElement,
        stateMachines: this.stateMachines,
        autoplay: this.autoplay,
        assetLoader: this.assetLoader.bind(this),
        onLoad: async (e: Event) => {
          await Promise.all(this.assetPromises);
          console.log('Video animation loaded successfully.');
          this.setTextsValues();
          this.setInputsValues();
          this.setEventHandler();
          this.isLoading = false;
          if (this.autoplay) {
            this.play();
          }
        },
      });
    } catch (error) {
      console.error('Error loading Video animation:', error);
      this.isLoading = false;
    }
  }
}
